// Questionnaire validation for the Hedging Grid CoPilot (CR-20260619-001, M2-2/3/4/5).
//
// PRESENCE + SANITY gating ONLY (FR-23..28). The host does NOT judge trade quality — a
// structurally-valid-but-poor questionnaire proceeds; the external skill is the only judge.
// These are pure functions (no Angular, no DOM) so they are unit-tested directly
// (TC-U-F01/F02/F03) and reused to gate the RUN SKILL control (M2-6).

import {
    AdjustInputs,
    CloseInputs,
    MaxRiskCeiling,
    PriceRange,
    RelativeRiskLevel,
    SelectDate,
    SelectInputs
} from './copilot-domain';

// ---- local helpers (deliberately self-contained: keep pure-logic specs DOM/dependency-free) ----

function isFiniteNumber(n: number): boolean {
    return typeof n === 'number' && isFinite(n);
}

// Widened to accept null/undefined (CR-20260814-001): AdjustInputs.expiration is nullable until the
// trader picks one, and "not picked yet" must read as invalid rather than as a type error.
function isValidDate(d: Date | null | undefined): boolean {
    return d instanceof Date && !isNaN(d.getTime());
}

// ---- PriceRange (FR-6/7/8/9/26; TC-U-F01/F02) ----

// The discriminated union must carry exactly one well-formed variant. 'bounded' requires
// start < end (TC-U-F02; equal strikes are INVALID). Unknown discriminant => invalid.
//
// 'fillGap' (CR-20260724-001) takes the SAME strike pair and the SAME rule — the two forms differ in
// what the engine may do OUTSIDE the pair, which is not a presence-or-sanity question and so is not
// judged here. Deliberately a separate case rather than a fall-through, so widening one form's rule
// later cannot silently widen the other's.
export function isPriceRangeValid(pr: PriceRange): boolean {
    if (pr == null) {
        return false;
    }
    switch (pr.kind) {
        case 'bounded':
            // CR-20260820-002: an expected-range Range asks the BACKEND for its edges, so there are
            // deliberately no strikes to judge here - the form is complete the moment the box is
            // ticked. Without this arm the panel would clear both dropdowns and then leave RUN
            // disabled with nothing on screen to fix, which is the worst of both.
            //
            // Nothing weaker is needed and nothing stronger is possible: whether the band RESOLVES
            // is a market question, answered where the market is read, and it comes back as a
            // named refusal on the run.
            if (pr.useExpectedRange) {
                return true;
            }
            return isFiniteNumber(pr.startStrike) && isFiniteNumber(pr.endStrike) && pr.startStrike < pr.endStrike;
        case 'fillGap':
            return isFiniteNumber(pr.startStrike) && isFiniteNumber(pr.endStrike) && pr.startStrike < pr.endStrike;
        case 'followThrough': {
            // CR-20260824-015: at least one side on; every enabled side complete (a finite strike
            // and a POSITIVE minimum — the backend refuses a non-positive side minimum by name,
            // so the gate keeps RUN honest rather than letting the click bounce); DOWN at or
            // below UP (equal strikes make the seamless line).
            if (pr.up == null && pr.down == null) {
                return false;
            }
            const sideComplete = (s: { startStrike: number; minProfit: number } | null) =>
                s == null || (isFiniteNumber(s.startStrike) && isFiniteNumber(s.minProfit) && s.minProfit > 0);
            if (!sideComplete(pr.up) || !sideComplete(pr.down)) {
                return false;
            }
            if (pr.up != null && pr.down != null && pr.down.startStrike > pr.up.startStrike) {
                return false;
            }
            return true;
        }
        case 'wall':
            return true;
        default:
            return false;
    }
}

// ---- Max-risk ceiling (FR-11/12/13; TC-U-F03) ----

export const RELATIVE_RISK_LEVELS: RelativeRiskLevel[] = ['Low', 'Moderate', 'Aggressive'];

// 'All' is NOT a fourth risk appetite, which is why it is not in the list above: it asks for every
// level in one run so the answers arrive together (owner, 2026-08-17). Kept separate so anything
// reasoning about how much risk was chosen still sees exactly three answers, while validation and
// the questionnaire can accept the extra request. Adjust tab only — the Select tab's engine does not
// know the value, so the questionnaire opts in rather than out.
export const ALL_RISK_LEVELS_REQUEST: RelativeRiskLevel = 'All';

// Legacy labels alias-map to the 3 canonical levels (FR-13). Canonical renamed 2026-07-09 (owner)
// Conservative/Moderate/Aggressive -> Low/Moderate/Aggressive, resolving the previously-inferred
// pairing (OQ-24 / R-9): Conservative == Low (least risk), High == Aggressive (most risk). BOTH prior
// vocabularies are accepted so persisted/legacy input still normalizes.
const RELATIVE_LEVEL_ALIASES: { [key: string]: RelativeRiskLevel } = {
    low: 'Low',
    moderate: 'Moderate',
    aggressive: 'Aggressive',
    conservative: 'Low',     // legacy alias (former canonical, renamed 2026-07-09)
    medium: 'Moderate',      // legacy alias
    high: 'Aggressive',      // legacy alias
    all: 'All'               // every level in one run (2026-08-17) — a request, not an appetite
};

// Resolve any input label (canonical or legacy) to a canonical level, or null if unknown.
export function normalizeRelativeLevel(input: string): RelativeRiskLevel | null {
    if (typeof input !== 'string' || input.length === 0) {
        return null;
    }
    const resolved = RELATIVE_LEVEL_ALIASES[input.trim().toLowerCase()];
    return resolved ? resolved : null;
}

export function isMaxRiskCeilingValid(mr: MaxRiskCeiling): boolean {
    if (mr == null) {
        return false;
    }
    switch (mr.kind) {
        // ZERO IS A REAL ANSWER, not a missing one (owner, 2026-07-27). A dollar max risk is a
        // FLOOR on the line, and on a Range run it is also how far beyond the range the engine may
        // sell — so zero means "nothing beyond the range", which is a common thing to want and was
        // previously impossible to express: > 0 made the whole Adjust questionnaire incomplete, so
        // Run was blocked rather than the field simply being rejected. Negative is still invalid;
        // the sign is carried by the concept, not typed by the trader.
        case 'dollar':
            return isFiniteNumber(mr.amount) && mr.amount >= 0;
        case 'relative':
            return RELATIVE_RISK_LEVELS.indexOf(mr.level) >= 0
                || mr.level === ALL_RISK_LEVELS_REQUEST;
        default:
            return false;
    }
}

// ---- Date selection (Select tab; FR-5) ----

export function isSelectDateValid(d: SelectDate): boolean {
    if (d == null) {
        return false;
    }
    if (d.kind === 'single') {
        return isValidDate(d.date);
    }
    if (d.kind === 'range') {
        return isValidDate(d.start) && isValidDate(d.end) && d.start.getTime() < d.end.getTime();
    }
    return false;
}

function isMinProfitValid(minProfit: number): boolean {
    return isFiniteNumber(minProfit) && minProfit >= 0;
}

// ---- Per-tab completeness (drives RUN SKILL enable; FR-23..28) ----

export function isSelectComplete(i: SelectInputs): boolean {
    return i != null
        && isSelectDateValid(i.date)
        && isPriceRangeValid(i.priceRange)
        && isMinProfitValid(i.minProfit)
        && isMaxRiskCeilingValid(i.maxRisk);
}

export function isAdjustComplete(i: AdjustInputs): boolean {
    return i != null
        // The expiration is MANDATORY (CR-20260814-001): it names the P&L line the run addresses.
        // This is the whole Run-button gate — hg-copilot.component's canRun already reads the active
        // tab's `valid`, so no change is needed there.
        && isValidDate(i.expiration)
        && isPriceRangeValid(i.priceRange)
        && isMinProfitValid(i.minProfit)
        && isMaxRiskCeilingValid(i.maxRisk);
}

// Presence/shape only. Existence against the actual book (dates + hedge ids) is a separate
// pre-send guard (validateCloseAgainstBook) so the UI can constrain selection AND the send
// path can double-check (FR-21/22/27/28).
export function isCloseComplete(i: CloseInputs): boolean {
    if (i == null || i.dates == null || i.dates.length === 0) {
        return false;
    }
    if (!i.dates.every(isValidDate)) {
        return false;
    }
    if (i.target == null) {
        return false;
    }
    if (i.target.kind === 'all') {
        return true;
    }
    if (i.target.kind === 'hedges') {
        return i.target.hedgeIds != null && i.target.hedgeIds.length > 0;
    }
    return false;
}

// Pre-send existence guard for Close (FR-21/22/27/28): every chosen date must exist in the
// book and (when targeting specific hedges) every hedge id must exist. Dates compared by
// calendar day (book dates are date-only). Returns true only when ALL targets exist.
export function validateCloseAgainstBook(
    i: CloseInputs,
    bookDates: Date[],
    bookHedgeIds: string[]
): boolean {
    if (!isCloseComplete(i)) {
        return false;
    }
    const bookDayKeys: { [key: string]: boolean } = {};
    bookDates.forEach(d => {
        if (isValidDate(d)) {
            bookDayKeys[dayKey(d)] = true;
        }
    });
    const allDatesExist = i.dates.every(d => bookDayKeys[dayKey(d)] === true);
    if (!allDatesExist) {
        return false;
    }
    if (i.target.kind === 'hedges') {
        const bookHedgeSet: { [key: string]: boolean } = {};
        bookHedgeIds.forEach(id => { bookHedgeSet[id] = true; });
        return i.target.hedgeIds.every(id => bookHedgeSet[id] === true);
    }
    return true;
}

function dayKey(d: Date): string {
    // calendar-day key, local — book dates are date-only (avoids time-of-day mismatch)
    return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
}
